import { useCallback } from "react";
import type { ChangeEvent, KeyboardEvent } from "react";

import { useLocalization } from "@fluent/react";
import { Button, Chip, TextInput } from "@narmi/design_system";
import { useNotificationContext } from "cerulean";

import {
  TAG_MAX_LENGTH,
  useTransactionTags,
} from "../../hooks/useTransactionTags";
import PanelCard from "../PanelCard/PanelCard";
import PanelSectionHeading from "../TransactionDetailsPanel/PanelSectionHeading";

import styles from "./TagsPanelSection.module.scss";

import type { TagError } from "../../hooks/useTransactionTags";
import type { ReactLocalization } from "@fluent/react";

const errorMessage = (
  error: TagError | null,
  l10n: ReactLocalization
): string | undefined => {
  switch (error) {
    case "empty":
      return l10n.getString("live-ledger-panel-tags-error-empty");
    case "duplicate":
      return l10n.getString("live-ledger-panel-tags-error-duplicate");
    case "tooLong":
      return l10n.getString("live-ledger-panel-tags-error-length", {
        charLimit: TAG_MAX_LENGTH,
      });
    default:
      return undefined;
  }
};

interface TagsPanelSectionProps {
  transaction: API.Transaction;
}

const TagsPanelSection = ({ transaction }: TagsPanelSectionProps) => {
  const { l10n } = useLocalization();
  const { sendNotification } = useNotificationContext();

  const onSaveError = useCallback(() => {
    sendNotification({
      type: "negative",
      text: l10n.getString("live-ledger-tags-save-error"),
    });
  }, [l10n, sendNotification]);

  const { draft, error, isSaving, onDraftChange, addTag, removeTag } =
    useTransactionTags(transaction, { onSaveError });

  if (!transaction.settled_at) {
    return null;
  }

  const tags = transaction.tags ?? [];

  return (
    <>
      <PanelSectionHeading
        title={l10n.getString("live-ledger-panel-tags-title")}
      />
      <PanelCard testId="panel-tags-card">
        {tags.length > 0 && (
          <div className={styles.chips}>
            {tags.map((tag) => (
              <div
                key={tag}
                role="group"
                aria-label={tag}
                data-testid={`panel-tags-chip-${tag}`}
              >
                <Chip label={tag} onDismiss={() => removeTag(tag)} />
              </div>
            ))}
          </div>
        )}
        <div className={styles.entry}>
          <div className={styles.field}>
            <TextInput
              label={l10n.getString("live-ledger-panel-tags-input-label")}
              value={draft}
              // Truncated here rather than through NDS `maxLength`, which is stripped before it
              // reaches the input, so it renders a counter the design lacks and stops nothing.
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                onDraftChange(event.target.value.slice(0, TAG_MAX_LENGTH))
              }
              onKeyDown={(event: KeyboardEvent<HTMLInputElement>) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  addTag();
                }
              }}
              error={errorMessage(error, l10n)}
              testId="panel-tags-input"
            />
          </div>
          <Button
            kind="secondary"
            type="button"
            label={l10n.getString("live-ledger-panel-tags-add-button")}
            onClick={addTag}
            disabled={draft.trim().length === 0 || isSaving}
            testId="panel-tags-add-button"
          />
        </div>
      </PanelCard>
    </>
  );
};

export default TagsPanelSection;
